import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { requestNotificationPermission, onForegroundMessage } from '@/lib/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState('');
  const [detectedAddress, setDetectedAddress] = useState('');
  const [userCoords, setUserCoords] = useState(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const locationDetectedRef = React.useRef(false);
  const gpsConfirmedRef = React.useRef(false);

  const reverseGeocode = useCallback(async (latitude, longitude) => {
    const waitForGoogle = () => new Promise((resolve, reject) => {
      let elapsed = 0;
      const check = () => {
        if (window.google && window.google.maps) return resolve();
        elapsed += 200;
        if (elapsed > 8000) return reject(new Error('Google Maps not loaded'));
        setTimeout(check, 200);
      };
      check();
    });
    try {
      await waitForGoogle();
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve) => {
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const components = results[0].address_components || [];
            let city = '', locality = '';
            for (const c of components) {
              if (c.types.includes('locality')) city = c.long_name;
              if (c.types.includes('sublocality_level_1') || c.types.includes('sublocality')) locality = c.long_name;
              if (!city && c.types.includes('administrative_area_level_2')) city = c.long_name;
            }
            const shortAddress = locality ? `${locality}, ${city}` : city;
            resolve({ city, address: shortAddress || results[0].formatted_address?.split(',').slice(0, 2).join(',') || '' });
          } else {
            resolve(null);
          }
        });
      });
    } catch {
      return null;
    }
  }, []);

  const requestBrowserLocation = useCallback((highAccuracy = false, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: highAccuracy, timeout, maximumAge: highAccuracy ? 0 : 120000 }
      );
    });
  }, []);

  const detectUserLocation = useCallback(async () => {
    if (locationDetectedRef.current) return;
    locationDetectedRef.current = true;
    setLocationLoading(true);

    // Step 1: Quick IP-based fallback (instant, runs in background)
    const ipFallback = async () => {
      try {
        const res = await fetch(`${API}/detect-location`);
        const data = await res.json();
        if (data.success && data.city) {
          setSelectedCity(prev => prev || data.city);
          setDetectedAddress(prev => prev || `${data.city}, ${data.region}`);
          if (data.latitude && data.longitude) {
            setUserCoords(prev => prev?.latitude ? prev : { latitude: data.latitude, longitude: data.longitude });
          }
        }
      } catch { /* ignore */ }
    };
    ipFallback();

    // Step 2: Try browser geolocation — low accuracy first (WiFi/cell tower, fast)
    try {
      const coords = await requestBrowserLocation(false, 6000);
      setUserCoords(coords);
      setLocationDenied(false);
      setLocationLoading(false);
      setShowLocationPrompt(false);
      gpsConfirmedRef.current = true;

      // Reverse geocode
      const geo = await reverseGeocode(coords.latitude, coords.longitude);
      if (geo?.city) {
        setSelectedCity(geo.city);
        setDetectedAddress(geo.address);
        localStorage.setItem('nd_city_chosen', 'true');
      }

      // Step 3: Upgrade with high accuracy GPS in background (mobile)
      try {
        const preciseCoords = await requestBrowserLocation(true, 12000);
        setUserCoords(preciseCoords);
        const preciseGeo = await reverseGeocode(preciseCoords.latitude, preciseCoords.longitude);
        if (preciseGeo?.city) {
          setSelectedCity(preciseGeo.city);
          setDetectedAddress(preciseGeo.address);
        }
      } catch { /* GPS upgrade failed, low-accuracy is fine */ }

    } catch (err) {
      // Browser location denied or timed out
      setLocationDenied(true);
      setLocationLoading(false);

      // Show prompt if user hasn't manually chosen a city before
      const hasChosenCity = localStorage.getItem('nd_city_chosen');
      if (!hasChosenCity) {
        setShowLocationPrompt(true);
      }
    }
  }, [requestBrowserLocation, reverseGeocode]);

  // Re-trigger location detection when tab becomes visible (fixes mobile preloaded tab issue)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !gpsConfirmedRef.current) {
        const hasChosenCity = localStorage.getItem('nd_city_chosen');
        if (!hasChosenCity) {
          // Tab just became active, user hasn't chosen city, GPS didn't work — show prompt
          setShowLocationPrompt(true);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Also: If page loaded but geolocation is still pending after 3s on mobile, show prompt
    const mobileTimer = setTimeout(() => {
      if (!gpsConfirmedRef.current) {
        const hasChosenCity = localStorage.getItem('nd_city_chosen');
        if (!hasChosenCity) {
          setShowLocationPrompt(true);
          setLocationLoading(false);
        }
      }
    }, 3500);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(mobileTimer);
    };
  }, []);

  // Manually retry location (called from the prompt/banner)
  const retryLocation = useCallback(async () => {
    setLocationLoading(true);
    setShowLocationPrompt(false);
    locationDetectedRef.current = false;
    try {
      const coords = await requestBrowserLocation(false, 8000);
      setUserCoords(coords);
      setLocationDenied(false);
      setLocationLoading(false);
      gpsConfirmedRef.current = true;

      const geo = await reverseGeocode(coords.latitude, coords.longitude);
      if (geo?.city) {
        setSelectedCity(geo.city);
        setDetectedAddress(geo.address);
        localStorage.setItem('nd_city_chosen', 'true');
      }

      // Upgrade in background
      try {
        const preciseCoords = await requestBrowserLocation(true, 12000);
        setUserCoords(preciseCoords);
        const preciseGeo = await reverseGeocode(preciseCoords.latitude, preciseCoords.longitude);
        if (preciseGeo?.city) {
          setSelectedCity(preciseGeo.city);
          setDetectedAddress(preciseGeo.address);
        }
      } catch { /* fine */ }
    } catch {
      setLocationDenied(true);
      setLocationLoading(false);
      setShowLocationPrompt(true);
    }
  }, [requestBrowserLocation, reverseGeocode]);

  const dismissLocationPrompt = useCallback(() => {
    setShowLocationPrompt(false);
    localStorage.setItem('nd_city_chosen', 'true');
  }, []);

  const checkAuth = useCallback(async () => {
    // Handle Emergent Google OAuth callback — extract session_id from hash
    if (window.location.hash?.includes('session_id=')) {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');
      if (sessionId) {
        try {
          const res = await axios.post(`${API}/auth/session?session_id=${sessionId}`, {}, { withCredentials: true });
          setUser(res.data.user);
          if (res.data.user?.city) setSelectedCity(res.data.user.city);
          // Clean up the hash from URL
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (e) {
          console.error('Session creation failed:', e);
        }
      }
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      if (response.data?.city) {
        setSelectedCity(response.data.city);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleGoogleLogin = useCallback(() => {
    const redirectUrl = window.location.origin + '/nd-admin-access';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Register FCM token when user is logged in
  const fcmRegistered = useRef(false);
  useEffect(() => {
    if (!user || fcmRegistered.current) return;
    fcmRegistered.current = true;
    (async () => {
      try {
        const token = await requestNotificationPermission();
        if (token) {
          await axios.post(`${API}/fcm/register-token`, { token }, { withCredentials: true });
          console.log('FCM token registered');
        }
      } catch (e) { console.warn('FCM registration failed:', e); }
    })();
  }, [user]);

  // Handle foreground FCM messages
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      if (Notification.permission === 'granted') {
        new Notification(payload.notification?.title || 'NirmanDwar', {
          body: payload.notification?.body,
          icon: '/assets/nirmandwar_building.png',
        });
      }
    });
    return unsubscribe;
  }, []);

  // Auto-detect location on mount
  useEffect(() => {
    detectUserLocation();
  }, [detectUserLocation]);

  const updateLocation = async ({ city, address, latitude, longitude }) => {
    setSelectedCity(city);
    setDetectedAddress(address);
    setUserCoords({ latitude, longitude });
    localStorage.setItem('nd_city_chosen', 'true');
    setShowLocationPrompt(false);
    if (!user) return;
    try {
      await axios.post(`${API}/auth/complete-profile`, {
        user_type: user.user_type,
        city, latitude, longitude,
      }, { withCredentials: true });
      setUser(prev => ({ ...prev, city }));
    } catch {
      console.error('Error updating location');
    }
  };

  const updateCity = async (city) => {
    setSelectedCity(city);
    localStorage.setItem('nd_city_chosen', 'true');
    setShowLocationPrompt(false);
    if (!user) return;
    try {
      await axios.post(`${API}/auth/complete-profile`, {
        user_type: user.user_type,
        city,
      }, { withCredentials: true });
      setUser(prev => ({ ...prev, city }));
    } catch {
      console.error('Error updating city');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setSelectedCity('');
      setDetectedAddress('');
      setUserCoords(null);
      locationDetectedRef.current = false;
    } catch {
      console.error('Logout error');
    }
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, checkAuth, logout, handleGoogleLogin,
      selectedCity, setSelectedCity, updateCity,
      detectedAddress, setDetectedAddress,
      userCoords, updateLocation, locationDenied,
      locationLoading, showLocationPrompt,
      retryLocation, dismissLocationPrompt,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
